import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { tokens } from "../../theme";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import { DataGrid } from "@mui/x-data-grid";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";

const WayaBankRequestLogs = () => {
  const { success, snackError } = useAuth();
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();

  // Tab state: 0 = Transactions, 1 = B2B Subscriptions, 2 = Admin Accounts, 3 = Card PIN
  const [activeTab, setActiveTab] = useState(0);
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog State for Approve/Reject Comments
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(""); // "APPROVE" or "REJECT"
  const [dialogRecord, setDialogRecord] = useState(null);
  const [comment, setComment] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);

  // Fetch pending list for active tab
  const fetchTabDetails = useCallback(async () => {
    setLoading(true);
    setListData([]);
    try {
      if (activeTab === 0) {
        // Transaction Requests
        const response = await axiosPrivate.get(
          "/transaction-payment/api/v1/admin/maker-checker/pending"
        );
        const data = response?.data?.data || response?.data || [];
        setListData(data);
      } else if (activeTab === 1) {
        // B2B Subscriptions
        const response = await axiosPrivate.get(
          "/wayabank-b2b-product-middleware/api/v1/portal/product-subscriptions/admin/requests?page=0&size=100"
        );
        const data = response?.data?.data?.content || response?.data?.data || response?.data?.content || response?.data || [];
        setListData(data);
      } else if (activeTab === 2) {
        // Admin creations
        const response = await axiosPrivate.get(
          "/auth-serviceV3/api/v1/admin-creations?page=0&size=100"
        );
        const data = response?.data?.data?.content || response?.data?.data || response?.data?.content || response?.data || [];
        setListData(data);
      } else if (activeTab === 3) {
        // Card PIN Requests
        const response = await axiosPrivate.get(
          "/card-service/cms/admin/approvals?page=0&size=100"
        );
        const data = response?.data?.data?.content || response?.data?.data || response?.data?.content || response?.data || [];
        setListData(data);
      }
    } catch (error) {
      console.error("Error fetching request logs:", error);
      snackError(error?.response?.data?.message || "Failed to fetch logs.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, axiosPrivate, snackError]);

  useEffect(() => {
    fetchTabDetails();
  }, [fetchTabDetails]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleActionClick = (action, record) => {
    setDialogAction(action);
    setDialogRecord(record);
    setComment("");
    setOpenDialog(true);
  };

  const handleDialogSubmit = async (e) => {
    e.preventDefault();
    if (!comment && dialogAction === "REJECT") {
      snackError("Comment is required for rejection.");
      return;
    }
    setDialogLoading(true);
    try {
      if (activeTab === 0) {
        // Transaction Requests Maker-Checker
        const payload = {
          reference: dialogRecord.reference,
          action: dialogAction,
          comment: comment,
        };
        const res = await axiosPrivate.post(
          "/transaction-payment/api/v1/admin/maker-checker/checker/action",
          payload
        );
        if (res?.data?.success || res?.data?.status === true) {
          success(`Transaction Request ${dialogAction.toLowerCase()}d successfully!`);
          fetchTabDetails();
        } else {
          snackError(res?.data?.message || `Failed to ${dialogAction.toLowerCase()} request.`);
        }
      } else if (activeTab === 1) {
        // B2B Subscriptions
        const approved = dialogAction === "APPROVE";
        const res = await axiosPrivate.put(
          `/wayabank-b2b-product-middleware/api/v1/portal/product-subscriptions/admin/requests/${dialogRecord.requestId || dialogRecord.id}`,
          { approved, comment }
        );
        if (res?.data?.success || res?.data?.status === true) {
          success(`B2B Request ${dialogAction.toLowerCase()}d successfully!`);
          fetchTabDetails();
        } else {
          snackError(res?.data?.message || `Failed to ${dialogAction.toLowerCase()} request.`);
        }
      } else if (activeTab === 2) {
        // Admin creations
        const actionPath = dialogAction === "APPROVE" ? "approve" : "reject";
        const res = await axiosPrivate.post(
          `/auth-serviceV3/api/v1/admin-creations/${dialogRecord.id}/${actionPath}`,
          {},
          {
            params: {
              checkerRemarks: comment,
            },
          }
        );
        if (res?.data?.success || res?.data?.status === true) {
          success(`Admin creation request ${dialogAction.toLowerCase()}d successfully!`);
          fetchTabDetails();
        } else {
          snackError(res?.data?.message || `Failed to ${dialogAction.toLowerCase()} request.`);
        }
      } else if (activeTab === 3) {
        // Card PIN approvals
        const actionPath = dialogAction === "APPROVE" ? "approve" : "reject";
        const res = await axiosPrivate.put(
          `/card-service/cms/admin/approvals/${dialogRecord.id}/${actionPath}`,
          { reason: comment }
        );
        if (
          res?.data?.success ||
          res?.data?.status === true ||
          res?.data?.message?.toLowerCase()?.includes("success")
        ) {
          success(`Card PIN request ${dialogAction.toLowerCase()}d successfully!`);
          fetchTabDetails();
        } else {
          snackError(res?.data?.message || `Failed to ${dialogAction.toLowerCase()} request.`);
        }
      }
      setOpenDialog(false);
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "Failed to process request.");
    } finally {
      setDialogLoading(false);
    }
  };

  // Safe unique ID generator for DataGrid
  const getRowId = (row) => {
    return (
      row.id ||
      row.requestId ||
      row.reference ||
      row.productCode ||
      row.email ||
      Math.random().toString()
    );
  };

  // Columns definition dynamically based on active tab
  const getColumns = () => {
    switch (activeTab) {
      case 0:
        return [
          { field: "reference", headerName: "Reference", width: 220 },
          {
            field: "amount",
            headerName: "Amount",
            width: 150,
            renderCell: ({ row: { amount } }) => `₦${amount?.toLocaleString()}`,
          },
          { field: "transactionType", headerName: "Transaction Type", width: 200 },
          { field: "maker", headerName: "Maker", width: 180 },
          { field: "createdAt", headerName: "Date", width: 180 },
          {
            field: "action",
            headerName: "Action",
            width: 220,
            renderCell: ({ row }) => (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleActionClick("APPROVE", row)}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleActionClick("REJECT", row)}
                >
                  Reject
                </Button>
              </Box>
            ),
          },
        ];
      case 1:
        return [
          { field: "productCode", headerName: "Product", width: 180 },
          { field: "merchantId", headerName: "Merchant ID", width: 180 },
          { field: "status", headerName: "Status", width: 150 },
          { field: "createdAt", headerName: "Requested Date", width: 180 },
          {
            field: "action",
            headerName: "Action",
            width: 220,
            renderCell: ({ row }) => (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleActionClick("APPROVE", row)}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleActionClick("REJECT", row)}
                >
                  Reject
                </Button>
              </Box>
            ),
          },
        ];
      case 2:
        return [
          { field: "firstName", headerName: "First Name", width: 150 },
          { field: "lastName", headerName: "Last Name", width: 150 },
          { field: "email", headerName: "Email", width: 220 },
          {
            field: "roles",
            headerName: "Roles",
            width: 180,
            renderCell: ({ row: { roles } }) =>
              Array.isArray(roles) ? roles.join(", ") : roles || "N/A",
          },
          { field: "makerId", headerName: "Maker", width: 150 },
          { field: "createdAt", headerName: "Date", width: 180 },
          {
            field: "action",
            headerName: "Action",
            width: 220,
            renderCell: ({ row }) => (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleActionClick("APPROVE", row)}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleActionClick("REJECT", row)}
                >
                  Reject
                </Button>
              </Box>
            ),
          },
        ];
      case 3:
        return [
          { field: "id", headerName: "Approval ID", width: 130 },
          { field: "targetId", headerName: "Card ID", width: 130 },
          { field: "actionType", headerName: "Request Type", width: 180 },
          { field: "status", headerName: "Status", width: 150 },
          { field: "makerEmail", headerName: "Maker (Initiator)", width: 200 },
          { field: "checkerEmail", headerName: "Checker", width: 200 },
          { field: "rejectionReason", headerName: "Rejection Reason", width: 180 },
          { field: "createdAt", headerName: "Date", width: 180 },
          {
            field: "action",
            headerName: "Action",
            width: 220,
            renderCell: ({ row }) => {
              const isPending = !row.status || row.status === "PENDING";
              return (
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={!isPending}
                    onClick={() => handleActionClick("APPROVE", row)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={!isPending}
                    onClick={() => handleActionClick("REJECT", row)}
                  >
                    Reject
                  </Button>
                </Box>
              );
            },
          },
        ];
      default:
        return [];
    }
  };

  return (
    <Box m="20px">
      <Header title="REQUEST LOGS" subtitle="Manage and verify checker requests" />

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid xs={12} sm={6} md={3}>
          <Box
            width="100%"
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={2}
            borderRadius="4px"
          >
            <StatBox
              title={listData.length}
              subtitle="Pending in Tab"
              icon={
                <PlaylistAddIcon
                  sx={{ color: colors.orangeAccent[600], fontSize: "26px" }}
                />
              }
            />
          </Box>
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <Box
            width="100%"
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={2}
            borderRadius="4px"
          >
            <StatBox
              title={activeTab === 0 ? "Transactions" : activeTab === 1 ? "B2B Subscriptions" : activeTab === 2 ? "Admin Creations" : "Card PIN"}
              subtitle="Active Approval Category"
              icon={
                <AssignmentTurnedInIcon
                  sx={{ color: colors.orangeAccent[600], fontSize: "26px" }}
                />
              }
            />
          </Box>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab label="Transaction Requests" />
        <Tab label="B2B Subscriptions" />
        <Tab label="Admin Accounts" />
        <Tab label="Card PIN Requests" />
      </Tabs>

      {/* List / DataGrid container */}
      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.orangeAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.redAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.redAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.orangeAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={listData}
          columns={getColumns()}
          loading={loading}
          getRowId={getRowId}
          pageSize={100}
        />
      </Box>

      {/* Decision Dialog for Checker remarks */}
      <Dialog
        open={openDialog}
        onClose={() => !dialogLoading && setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handleDialogSubmit}>
          <DialogTitle>
            {dialogAction === "APPROVE" ? "Approve Request" : "Reject Request"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to {dialogAction.toLowerCase()} this request?
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label={dialogAction === "REJECT" ? "Rejection Reason (Required)" : "Comments (Optional)"}
              variant="outlined"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required={dialogAction === "REJECT"}
              disabled={dialogLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDialog(false)}
              disabled={dialogLoading}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color={dialogAction === "APPROVE" ? "success" : "error"}
              variant="contained"
              disabled={dialogLoading}
            >
              {dialogLoading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default WayaBankRequestLogs;
